import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import MarquetCards from '../../../utils/components/MarquetCards';
import Loader from '../../../assets/components/loader';
import CreditCard from '../../../assets/svg/creditCard';

import BillingActivitySkeleton from './billingActivitySkeleton';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faAngleRight, faAngleDown } from '@fortawesome/free-solid-svg-icons';

import { DateTime } from 'luxon';

import {
  getInvoices,
  getCustomerSubscriptions,
  getCustomerCard,
  downloadInvoice,
  updateCustomerCard,
  resetUrl
} from "../../../store/actions";
import { getCustomer } from '../../../utils/helpers';

const BillingActivity = () => {

  const customerId = getCustomer();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const billingActivityInfo = useSelector((state) => state.billingActivity);

  const [invoiceType, setInvoiceType] = useState(1)
  const [openSubscription, setOpenSubscription] = useState(null)
  const [card, setCard] = useState({
    firstName: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    lastName: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cardNumber: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    expiryMonth: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    expiryYear: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cvc: {
      value: "",
      focused: false,
      required: true,
      error: ""
    }
  });

  useEffect(() => {
    if (customerId === "") {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    if (!billingActivityInfo.invoicesObtained){
      dispatch(getInvoices(20));
    }
  }, [billingActivityInfo.invoicesObtained])

  useEffect(() => {
    if (!billingActivityInfo.subscriptionsObtained) {
      dispatch(getCustomerSubscriptions());
    }
  }, [billingActivityInfo.subscriptionsObtained])

  useEffect(() => {
    if (!billingActivityInfo.cardObtained){
      dispatch(getCustomerCard());
    }else{
      let currentKeys = Object.keys(card)
      currentKeys.map(key => {
        if (billingActivityInfo.card.hasOwnProperty([key]) && key) {
          setCard(prevCard => ({
            ...prevCard,
            [key]: {
              ...prevCard[key],
              value: billingActivityInfo.card[key],
              focused: true,
              error: prevCard[key].required ? validate(key, billingActivityInfo.card[key]) : ""
            }
          }))
        }else if(key === "cardNumber"){
          setCard(prevCard => ({
            ...prevCard,
            [key]: {
              ...prevCard[key],
              value: billingActivityInfo.card.maskedNumber,
              focused: false,
              error: ""
            }
          }))
        }
      })
    }
  }, [billingActivityInfo.cardObtained])

  const validate = (name, value) => {
    switch (name) {
      case ("cardName"):
        if (value.length === 0) {
          return "Card name is required"
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          return "Card name is not valid"
        } else {
          return ""
        }
      case ("expiryMonth"):
        if (value.length === 0) {
          return "Expiry month is required"
        } else if (value < new Date().getMonth() && card.expiryYear.value === new Date().getFullYear()) {
          return "The card can not be expired"
        } else {
          return ""
        }
      case ("expiryYear"):
        if (value.length === 0) {
          return "Expiry year is required"
        } else {
          return ""
        }
      case ("cardNumber"):
        if (value.length === 0) {
          return "Card number is required"
        } else if (!/^[0-9]{16}$/.test(value)) {
          return "Card number is not valid"
        } else {
          return ""
        }
      case ("cvc"):
        if (value.length === 0) {
          return "CVC is required"
        } else if (!/^[0-9]{3}$/.test(value)) {
          return "CVC is not valid"
        } else {
          return ""
        }
      default:
        return ""
    }
  }

  const handleInputChange = (ev) => {
    setCard(prevCard => ({
      ...prevCard,
      [ev.target.name]: {
        ...prevCard[ev.target.name],
        value: ev.target.value,
        focused: true,
        error: prevCard[ev.target.name].required ? validate(ev.target.name, ev.target.value) : ""
      }
    }))
  }

  const handleOnfocus = (ev) => {
    setCard(prevCard => ({
      ...prevCard,
      [ev.target.name]: {
        ...prevCard[ev.target.name],
        focused: true,
        error: prevCard[ev.target.name].required ? validate(ev.target.name, ev.target.value) : ""
      }
    }))
  }

  const inputStatus = (key) => {
    if (card[key].focused) {
      if (card[key].error === "") {
        return "input--group input--success"
      } else {
        return "input--group input--error"
      }
    } else {
      return "input--group"
    }
  }

  function disabledSubmit() {
    if(billingActivityInfo.loading){
      return true
    }
    let keys = Object.keys(card)
    for (let i = 0; i < keys.length; i++) {
      if (card[keys[i]].required) {
        if (card[keys[i]].focused && card[keys[i]].error !== "") {
          return true
        } else if (!card[keys[i]].focused) {
          return true
        }
      }
    }
    return false
  }

  const renderCardYear = () => {
    let n = 15
    let currenYear = new Date().getFullYear()
    let arr = [...Array(n).keys()];
    return arr.map((year, index) => (
      <option key={index} value={currenYear + year}>
        {currenYear + year}
      </option>
    ))
  }

  const renderCardMonth = () => {
    let n = 12
    let arr = [...Array(n).keys()];
    return arr.map((month, index) => (
      <option key={index} value={month + 1}>
        {month + 1}
      </option>
    ))
  }

  function formatDate(timestamp) {
    if (typeof (timestamp) === "number") {
      const date = DateTime.fromSeconds(timestamp);
      const formattedDate = date.toFormat("MMMM dd, yyyy");
      return formattedDate;
    } else {
      return ""
    }
  }

  function downloadingInvoice(id){
    dispatch(downloadInvoice(id));
  }

  function changeInvoiceType(type){
    setInvoiceType(type);
  }

  function viewSubscriptionInvoices(id){
    setOpenSubscription(id);
  }

  function calculateSubscriptionValue(subscription){
    let value = 0;
    if (billingActivityInfo && billingActivityInfo.invoices && billingActivityInfo.invoices.length > 0){
      billingActivityInfo.invoices.map((it) => {
        if (subscription.id === it.subscriptionId){
          value += it.amountPaid
        }
      })
      return (value / 100).toLocaleString('en-US', { style: 'currency', currency: subscription.currencyCode })
    }else{
      return (value / 100).toLocaleString('en-US', { style: 'currency', currency: subscription.currencyCode })
    }
  }

  useEffect(() => {
    if (billingActivityInfo.downloadUrl !== ""){
      const link = document.createElement('a');
      link.href = billingActivityInfo.downloadUrl;
      link.setAttribute(
        'download',
        `invoice${billingActivityInfo.downloadUrl}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      dispatch(resetUrl());
    }
  }, [billingActivityInfo.downloadUrl])

  const updateCard = (ev) =>{
    ev.preventDefault();
    let dataCard = {
      replace_primary_payment_source: true,
      card: {
        first_name: card.firstName.value,
        last_name: card.lastName.value,
        number: card.cardNumber.value,
        cvv: card.cvc.value,
        expiry_month: parseInt(card.expiryMonth.value),
        expiry_year: parseInt(card.expiryYear.value)
      },
      update : true
    }
    dispatch(updateCustomerCard(dataCard));
  }

  if (!billingActivityInfo.loadedFirstTime) {
    return (
      <BillingActivitySkeleton/>
    )
  }

  return (
    <>
      <div className="billings">
        <MarquetCards classStyles="card--side">
          <div className="top--card--title">
            <span>Payment Method</span>
          </div>
          <div className="card--icon--container">
            <CreditCard
              type={billingActivityInfo && billingActivityInfo.card && billingActivityInfo.card.cardType ? billingActivityInfo.card.cardType : ""}
              cardNumber={billingActivityInfo && billingActivityInfo.card && billingActivityInfo.card.maskedNumber ? billingActivityInfo.card.maskedNumber: "0000 0000 0000 0000"}
              expirationDate={billingActivityInfo && billingActivityInfo.card && billingActivityInfo.card.expiryMonth && billingActivityInfo.card.expiryYear? `${billingActivityInfo.card.expiryMonth}/${billingActivityInfo.card.expiryYear.toString().slice(-2)}`: "MM/YY"}
            />
          </div>
          <form className="marquet--form" onSubmit={ev => updateCard(ev)}>
            <div className="marquet--form--group">
              <div className={inputStatus("firstName")}>
                <label>
                  FIRST NAME<b> *</b>
                </label>
                <input
                  name="firstName"
                  type="text"
                  maxLength={30}
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={card.firstName.value} />
              </div>
              <div className="invalid--feedback">{card.firstName.error}</div>
            </div>
            <div className="marquet--form--group">
              <div className={inputStatus("lastName")}>
                <label>
                  LAST NAME<b> *</b>
                </label>
                <input
                  name="lastName"
                  type="text"
                  maxLength={30}
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={card.lastName.value} />
              </div>
              <div className="invalid--feedback">{card.lastName.error}</div>
            </div>
            <div className="marquet--form--group">
              <div className={inputStatus("cardNumber")}>
                <label>
                  CREDIT CARD NUMBER<b> *</b>
                </label>
                <input
                  name="cardNumber"
                  type="text"
                  maxLength={16}
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={card.cardNumber.value} />
              </div>
              <div className="invalid--feedback">{card.cardNumber.error}</div>
            </div>
            <div className="marquet--form--group">
              <div className={
                card.expiryMonth.focused || card.expiryYear.focused ?
                  card.expiryMonth.error === "" && card.expiryYear.error === "" ?
                    "input--group input--success" : "input--group input--error"
                  : "input--group"
              }>
                <label>
                  EXPIRY DATE<b> *</b>
                </label>
                <div className="row--form">
                  <select
                    name="expiryYear"
                    type="text"
                    style={{ width: "50%" }}
                    onChange={ev => handleInputChange(ev)}
                    onFocus={ev => handleOnfocus(ev)}
                    value={card.expiryYear.value} >
                    <option value="">YY</option>
                    {renderCardYear()}
                  </select>
                  <select
                    name="expiryMonth"
                    type="text"
                    style={{ width: "50%" }}
                    onChange={ev => handleInputChange(ev)}
                    onFocus={ev => handleOnfocus(ev)}
                    value={card.expiryMonth.value} >
                    <option value="">MM</option>
                    {renderCardMonth()}
                  </select>
                </div>
              </div>
              <div className="invalid--feedback">{`${card.expiryMonth.error} ${card.expiryYear.error}`}</div>
            </div>
            <div className="marquet--form--group">
              <div className={inputStatus("cvc")}>
                <label>
                  CVC<b> *</b>
                </label>
                <input
                  name="cvc"
                  type="password"
                  maxLength={3}
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={card.cvc.value} />
              </div>
              <div className="invalid--feedback">{card.cvc.error}</div>
            </div>
            <div className="marquet--button--group">
              <button
                className="custom--button custom--button--marquet"
                disabled={disabledSubmit()}
                type='submit'>
                Update my Payment Method
                {billingActivityInfo.loading && <Loader size="xs" />}
              </button>
            </div>
          </form>
        </MarquetCards>
        <div className="invoices">
          <div className="title">
            <h2>INVOICES</h2>
          </div>
          <div className="invoices--type">
            <div className={invoiceType === 1 ? "invoice--type active" :"invoice--type"} onClick={ev => changeInvoiceType(1)}>All Invoices</div>
            <div className={invoiceType === 2 ? "invoice--type active" : "invoice--type"} onClick={ev => changeInvoiceType(2)}>Invoices By Subscription</div>
            <div></div>
          </div>
          <div className="table--container">
            {invoiceType === 1 &&
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice</th>
                    <th>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {billingActivityInfo && billingActivityInfo.invoices && billingActivityInfo.invoices.length > 0 && billingActivityInfo.invoices.map((it, i) => (
                    <tr key={i}>
                      <td>{`${formatDate(it.date)}`}</td>
                      <td>{`${it.id}`}</td>
                      <td>{`${(it.amountPaid / 100).toLocaleString('en-US', { style: 'currency', currency: it.currencyCode })}`}</td>
                      <td>
                        {billingActivityInfo.downloadingId === it.id ?
                          <div className="download--cell disabled">
                            <Loader size="xs" />
                            <div>
                              Download
                            </div>
                          </div> :
                          <div className="download--cell" onClick={ev => downloadingInvoice(it.id)}>
                            <FontAwesomeIcon icon={faDownload} />
                            <div>
                              Download
                            </div>
                          </div>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
            {
              invoiceType === 2 &&
              <div className="subscription--invoices">
                {
                    billingActivityInfo && billingActivityInfo.subscriptions && billingActivityInfo.subscriptions.length > 0 &&
                    billingActivityInfo.subscriptions.map((it, i) => {
                      if(it.status === "active"){
                        return(
                          <>
                            <div key={i} className="subscription--invoice">
                              <div>
                                {openSubscription === it.id?
                                  <FontAwesomeIcon icon={faAngleDown} onClick={ev => viewSubscriptionInvoices(null)} />:
                                  <FontAwesomeIcon icon={faAngleRight} onClick={ev => viewSubscriptionInvoices(it.id)} />
                                }
                                {it.subscriptionItems[0].itemPriceId}
                              </div>
                              <div>{calculateSubscriptionValue(it)}</div>
                            </div>
                            {openSubscription === it.id &&
                              <table>
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Invoice</th>
                                    <th>Amount</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {billingActivityInfo && billingActivityInfo.invoices && billingActivityInfo.invoices.length > 0 && billingActivityInfo.invoices.map((jt, j) => {
                                    if (jt.subscriptionId === it.id) {
                                      return (
                                        <tr key={j}>
                                          <td>{`${formatDate(jt.date)}`}</td>
                                          <td>{`${jt.id}`}</td>
                                          <td>{`${(jt.amountPaid / 100).toLocaleString('en-US', { style: 'currency', currency: jt.currencyCode })}`}</td>
                                          <td>
                                            {billingActivityInfo.downloadingId === jt.id ?
                                              <div className="download--cell disabled">
                                                <Loader size="xs" />
                                                <div>
                                                  Download
                                                </div>
                                              </div> :
                                              <div className="download--cell" onClick={ev => downloadingInvoice(jt.id)}>
                                                <FontAwesomeIcon icon={faDownload} />
                                                <div>
                                                  Download
                                                </div>
                                              </div>
                                            }
                                          </td>
                                        </tr>
                                      )
                                    }
                                  })}
                                </tbody>
                              </table>
                            }
                          </>
                        )
                      }
                    })
                }
              </div>
            }
          </div>
        </div>
      </div>
    </>
  )
}

export default BillingActivity;